import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { getStatusColor, CONTRACT_STATUSES, ENTITIES } from "../../services/contractService";

const DEPARTMENTS = ["HR/Admin","Finance","Operations & Asset","IT","Corp. Comms","SCM","Legal","HSE","Commercial","Engineering","Executive"];

export const ContractList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { service, isUser, currentUser, contractTypes } = useApp();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ entity:"", status:"", type:"", department:"", year:"", procurementType:"" });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const itemsPerPage = 15;

  // Apply URL filter on mount
  useEffect(() => {
    const filterParam = searchParams.get("filter");
    if (filterParam) {
      const filterMap = {
        "addenda": { type: "Addendum" },
        "executed": { status: "Executed" },
        "inreview": { status: "In Review" },
        "pending": { status: "Pending Approval" },
        "expiring": { expiringFilter: true },
      };
      const newFilter = filterMap[filterParam] || {};
      if (newFilter.expiringFilter) {
        // For expiring, we'll just show a note - user can see expiring contracts on dashboard
        setShowFilters(true);
      } else {
        setFilters(p => ({...p, ...newFilter}));
        setShowFilters(true);
      }
    }
  }, [searchParams]);

 useEffect(() => {
  service.getListItems("Contracts").then(d => {
    let all = d?.value?.map(i => ({...i.fields, id:i.id})) || [];
    // All users can now see all contracts
    setContracts(all);
  }).catch(()=>setContracts([])).finally(()=>setLoading(false));
}, [service]);

  const sf = (k,v) => { setFilters(p=>({...p,[k]:v})); setPage(0); };

  const filtered = contracts.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || [c.Title,c.RefNo,c.VendorName,c.CategoryManager].some(s=>(s||"").toLowerCase().includes(q));
    const matchEntity = !filters.entity || c.Entity===filters.entity;
    const matchStatus = !filters.status || c.Status===filters.status;
    const matchType = !filters.type || c.ContractType===filters.type;
    const matchDept = !filters.department || c.Department===filters.department;
    const matchYear = !filters.year || c.Year===filters.year;
    const matchProcType = !filters.procurementType || c.ProcurementType===filters.procurementType;
    return matchSearch&&matchEntity&&matchStatus&&matchType&&matchDept&&matchYear&&matchProcType;
  });

  const paginated = filtered.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const years = [...new Set(contracts.map(c=>c.Year).filter(Boolean))].sort((a,b)=>b-a);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-light-text dark:text-dark-text m-0 mb-1">
          {isUser ? "My Contract Requests" : "Contracts Registry"}
        </h1>
        <p className="text-light-text-secondary dark:text-dark-text-secondary m-0">{filtered.length} contracts</p>
        <div className="h-0.5 bg-gradient-to-r from-blue-600 to-transparent mt-2"/>
      </div>

      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-xl p-4 mb-5">
        <div className={`flex gap-2.5 ${showFilters ? "mb-3.5" : ""}`}>
          <input
            value={search}
            onChange={e=>{setSearch(e.target.value); setPage(0);}}
            placeholder="Search title, ref no, vendor, manager..."
            className="flex-1 px-3.5 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
          <button
            onClick={()=>setShowFilters(!showFilters)}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              showFilters
                ? "bg-blue-600/15 border border-blue-600/20 text-blue-600"
                : "bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text dark:hover:text-dark-text"
            }`}
          >
            Filters
          </button>
          <button onClick={()=>navigate("/request")} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 border-none rounded-lg text-white font-bold text-sm transition-colors">+ New Request</button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
            <select value={filters.entity} onChange={e=>sf("entity",e.target.value)} className="px-2 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">All Entities</option>{ENTITIES.map(o=><option key={o}>{o}</option>)}
            </select>
            <select value={filters.status} onChange={e=>sf("status",e.target.value)} className="px-2 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">All Statuses</option>{Object.values(CONTRACT_STATUSES).map(o=><option key={o}>{o}</option>)}
            </select>
            <select value={filters.type} onChange={e=>sf("type",e.target.value)} className="px-2 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">All Types</option>{contractTypes.map(o=><option key={o}>{o}</option>)}
            </select>
            <select value={filters.department} onChange={e=>sf("department",e.target.value)} className="px-2 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">All Departments</option>{DEPARTMENTS.map(o=><option key={o}>{o}</option>)}
            </select>
            <select value={filters.year} onChange={e=>sf("year",e.target.value)} className="px-2 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">All Years</option>{years.map(y=><option key={y}>{y}</option>)}
            </select>
            <select value={filters.procurementType} onChange={e=>sf("procurementType",e.target.value)} className="px-2 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="">All Contract Types</option>
              {["One-off Service","Call-out Service","Framework Order"].map(o=><option key={o}>{o}</option>)}
            </select>
            <button onClick={()=>{setFilters({entity:"",status:"",type:"",department:"",year:"",procurementType:""}); setPage(0);}} className="px-2 py-2 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary text-sm hover:text-light-text dark:hover:text-dark-text transition-colors">Clear</button>
          </div>
        )}
      </div>

      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-xl overflow-hidden">
        {loading ? <div className="p-12 text-center text-light-text-secondary dark:text-dark-text-secondary">Loading contracts...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                  {["Ref. No.","Contract Title","Entity","Supplier","Type","Department","Status","Requested","Cycle"].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider border-b border-light-border dark:border-dark-border whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length===0 ? (
                  <tr><td colSpan={9} className="text-center py-12 text-light-text-secondary dark:text-dark-text-secondary">No contracts found.</td></tr>
                ) : paginated.map((c,i) => (
                  <tr key={i} onClick={()=>navigate("/contracts/"+c.id)} className={`cursor-pointer border-b border-light-border dark:border-dark-border transition-colors ${c.Status === "Close Out" ? "opacity-50 bg-gray-100/20 dark:bg-gray-100/10 hover:bg-gray-100/30 dark:hover:bg-gray-100/20" : "hover:bg-blue-600/5 dark:hover:bg-blue-600/5"}`}>
                    <td className="px-4 py-3 flex items-center gap-2">
                      {c.ContractType && c.ContractType.includes("Addendum") && <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-50 text-purple-700">ADDENDUM</span>}
                      <span className="font-mono text-sm font-bold text-gray-900">{c.RefNo||"--"}</span>
                    </td>
                    <td className="px-4 py-3 font-medium max-w-xs truncate">{c.Title||"Untitled"}</td>
                    <td className="px-4 py-3 text-light-text-secondary dark:text-dark-text-secondary">{c.Entity}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{c.VendorName}</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary">{c.ContractType}</span></td>
                    <td className="px-4 py-3 text-light-text-secondary dark:text-dark-text-secondary">{c.Department}</td>
                    <td className="px-4 py-3"><span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{c.Status||"--"}</span></td>
                    <td className="px-4 py-3 text-light-text-secondary dark:text-dark-text-secondary">{c.RequestDate?new Date(c.RequestDate).toLocaleDateString():"--"}</td>
                    <td className={`px-4 py-3 font-mono text-sm ${c.CycleDays>30?"text-red-700":"text-green-700"}`}>{c.CycleDays||"--"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-light-border dark:border-dark-border bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <div>Showing {page * itemsPerPage + 1} to {Math.min((page + 1) * itemsPerPage, filtered.length)} of {filtered.length}</div>
            <div className="flex gap-2">
              <button onClick={()=>setPage(0)} disabled={page===0} className={`px-3 py-1.5 rounded text-sm ${page===0 ? "bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-not-allowed" : "bg-light-bg-secondary dark:bg-dark-bg-secondary text-blue-600 hover:text-blue-500"}`}>First</button>
              <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} className={`px-3 py-1.5 rounded text-sm ${page===0 ? "bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-not-allowed" : "bg-light-bg-secondary dark:bg-dark-bg-secondary text-blue-600 hover:text-blue-500"}`}>Previous</button>
              <span className="px-3 py-1.5 text-blue-600 font-semibold">Page {page + 1} of {totalPages}</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page===totalPages-1} className={`px-3 py-1.5 rounded text-sm ${page===totalPages-1 ? "bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-not-allowed" : "bg-light-bg-secondary dark:bg-dark-bg-secondary text-blue-600 hover:text-blue-500"}`}>Next</button>
              <button onClick={()=>setPage(totalPages-1)} disabled={page===totalPages-1} className={`px-3 py-1.5 rounded text-sm ${page===totalPages-1 ? "bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-not-allowed" : "bg-light-bg-secondary dark:bg-dark-bg-secondary text-blue-600 hover:text-blue-500"}`}>Last</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
